import React from "react";
import {
  FiAlertCircle,
  FiClipboard,
  FiDollarSign,
  FiMail,
  FiMapPin,
  FiPhone,
  FiUsers,
} from "react-icons/fi";
import profileLead from "../../Assets/Profile/profile-lead.jpg";
import checkIn from "../../Assets/Profile/check-in.jpg";
import setupFlorals from "../../Assets/Profile/setup-florals.jpg";
import creativeBuild from "../../Assets/Profile/creative-build.jpg";
import balloonFinish from "../../Assets/Profile/balloon-finish.jpg";
import ceremony from "../../Assets/Profile/ceremony.jpg";
import outdoorDelivery from "../../Assets/Profile/outdoor-delivery.jpg";

const onSitePhotos = [
  { src: setupFlorals, className: "on-site-photo--portrait" },
  { src: checkIn, className: "on-site-photo--wide" },
  { src: creativeBuild, className: "on-site-photo--square" },
  { src: outdoorDelivery, className: "on-site-photo--square" },
  { src: ceremony, className: "on-site-photo--landscape" },
  { src: balloonFinish, className: "on-site-photo--landscape" },
];

const content = {
  zh: {
    title: "把想法整理成流程，\n把流程落到現場。",
    paragraphs: [
      "我是一名活動策劃與現場執行專業人士，過往服務範圍涵蓋地產推廣、商場活動、餐飲品牌活動、政府宣傳、企業交流及私人宴會。我的工作習慣是先釐清目標與受眾，再把創意、預算、時間表、供應商和現場動線逐步拆解，讓每一個環節都有清楚負責人和交付節點。",
      "在活動落地過程中，我重視溝通節奏和細節管理：從前期方案、物料準備、嘉賓接待，到現場突發情況處理，都會盡量提前預判並保持執行秩序。人事及行政管理背景，也讓我在跨部門協作、文件、合同、排期和團隊溝通上更穩健。",
      "我希望在新的活動崗位中，把過往 150+ 場活動的實戰經驗，轉化為可靠、細緻、能承接壓力的現場交付能力。",
    ],
    caption: "EUNICE LU / EVENT PLANNING",
    metrics: [
      { value: "150+", label: "場活動策劃及執行" },
      { value: "5", label: "家企業落地協調" },
      { value: "3", label: "個地產項目廣告總承包" },
      { value: "9+", label: "年跨部門管理經驗" },
    ],
    availability: "現居香港 · 可即時全職投入",
    capabilitiesTitle: "我能承接的工作",
    capabilitiesIntro:
      "從前期 brief 到現場交付，我習慣把複雜的事情拆成可以執行、可以追蹤、可以準時完成的步驟。",
    capabilities: [
      { icon: FiClipboard, title: "方案與流程", text: "把需求整理成提案、流程表、run down 和現場分工。" },
      { icon: FiDollarSign, title: "預算與供應商", text: "跟進報價、合同、採購、物料及每個交付節點。" },
      { icon: FiUsers, title: "現場與嘉賓", text: "安排場地動線、搭建、接待、嘉賓及流程節奏。" },
      { icon: FiAlertCircle, title: "應變與協調", text: "提前預判風險，現場快速判斷並協調跨部門處理。" },
    ],
    onSiteTitle: "在現場，也在細節裡",
    onSiteIntro:
      "從進場佈置、嘉賓接待到流程交付，我習慣留在第一線確認細節，讓創意真正成為可以被看見、被體驗的現場。",
    onSiteCaptions: [
      "佈置與細節調整",
      "嘉賓報到與流程協調",
      "創意製作與現場配合",
      "戶外活動交付確認",
      "典禮流程與團隊協作",
      "完成後的現場檢視",
    ],
  },
  en: {
    title: "Turning ideas into plans, and plans into live delivery.",
    paragraphs: [
      "I am an event planning and on-site delivery professional with experience across property campaigns, retail events, food and beverage activations, government communications, business forums and private celebrations. I begin by clarifying the brief and audience, then translate creative direction, budget, timelines, vendors and guest flow into a practical execution plan.",
      "On-site, I care about rhythm, coordination and detail. From proposals, materials and guest reception to last-minute issue handling, I work to keep people aligned and the event moving in an orderly way. My HR and administration background also supports stronger documentation, contracts, scheduling and cross-team communication.",
      "For my next event role, I hope to bring the practical judgement developed across 150+ delivered events: calm coordination, careful follow-through and reliable execution under pressure.",
    ],
    caption: "EUNICE LU / EVENT PLANNING",
    metrics: [
      { value: "150+", label: "events planned and delivered" },
      { value: "5", label: "business relocations coordinated" },
      { value: "3", label: "property projects under full contract" },
      { value: "9+", label: "years across team operations" },
    ],
    availability: "Based in Hong Kong · Available immediately",
    capabilitiesTitle: "Core capabilities",
    capabilitiesIntro:
      "From the first brief to the live floor, I turn complex moving parts into clear, trackable steps that arrive on time.",
    capabilities: [
      { icon: FiClipboard, title: "Plans & run sheets", text: "Turning a brief into proposals, timelines, run downs and ownership." },
      { icon: FiDollarSign, title: "Budgets & vendors", text: "Managing quotations, contracts, procurement, materials and delivery points." },
      { icon: FiUsers, title: "Guests & on-site flow", text: "Coordinating venue flow, builds, reception, guests and event rhythm." },
      { icon: FiAlertCircle, title: "Issues & alignment", text: "Anticipating risks, making calm decisions and aligning teams on site." },
    ],
    onSiteTitle: "Present on site, attentive to detail",
    onSiteIntro:
      "From build-in and guest reception to the final run of show, I stay close to the work and check the details that turn an idea into a well-delivered experience.",
    onSiteCaptions: [
      "Build and detail adjustments",
      "Guest check-in and flow",
      "Creative production on site",
      "Outdoor delivery check",
      "Ceremony and team coordination",
      "Final on-site review",
    ],
  },
};

function Profile({ language }) {
  const copy = content[language];

  return (
    <main className="profile-page">
      <section className="profile-hero site-container">
        <div className="profile-copy">
          <h1>{copy.title}</h1>
          {copy.paragraphs.map((paragraph) => (
            <p key={paragraph}>{paragraph}</p>
          ))}
          <div className="profile-meta">
            <span>
              <FiMapPin aria-hidden="true" />
              {copy.availability}
            </span>
            <a href="mailto:eunicelu312@outlook.com">
              <FiMail aria-hidden="true" />
              eunicelu312@outlook.com
            </a>
            <a href="tel:+85266872763">
              <FiPhone aria-hidden="true" />
              +852 6687 2763
            </a>
          </div>
        </div>
        <div className="profile-photo-stage">
          <img src={profileLead} alt={copy.caption} className="profile-photo" />
        </div>
      </section>

      <section className="profile-metrics">
        <div className="site-container metrics-grid metrics-grid--profile">
          {copy.metrics.map((metric) => (
            <div className="metric" key={metric.label}>
              <strong>{metric.value}</strong>
              <span>{metric.label}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="profile-capabilities site-container">
        <div className="profile-capabilities__intro">
          <h2>{copy.capabilitiesTitle}</h2>
          <p>{copy.capabilitiesIntro}</p>
        </div>
        <div className="capabilities-grid">
          {copy.capabilities.map(({ icon: Icon, title, text }) => (
            <article className="capability" key={title}>
              <Icon aria-hidden="true" />
              <div>
                <h3>{title}</h3>
                <p>{text}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="profile-on-site">
        <div className="site-container">
          <div className="profile-on-site__intro">
            <h2>{copy.onSiteTitle}</h2>
            <p>{copy.onSiteIntro}</p>
          </div>
          <div className="on-site-grid">
            {onSitePhotos.map((photo, index) => (
              <figure className={`on-site-photo ${photo.className}`} key={photo.src}>
                <div className="on-site-photo__image">
                  <img src={photo.src} alt={copy.onSiteCaptions[index]} loading="lazy" />
                </div>
                <figcaption>{copy.onSiteCaptions[index]}</figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}

export default Profile;
